import { useCallback, type Dispatch, type SetStateAction } from 'react'
import { useDefaultAssessmentTest, useStartAssessment } from '@/api/enrollment-api'
import {
  EnrollmentAssessmentDtoStatusEnum,
  type AssessmentAnswerDto,
  type EnglishTestDto
} from '@/api/generated/models'
import { useEnrollmentStore } from '@/stores'
import {
  ENGLISH_TEST_MESSAGES,
  ENROLLMENT_COMMON_MESSAGES,
  ENROLLMENT_PUBLIC_LINKS
} from '../../constants/enrollmentContent'
import type { EnglishProficiencyStepProps, EnglishTestResult } from './types'
import {
  createPassedAssessmentResult,
  formatStartTestErrorMessage,
  getErrorMessage,
  isRetryLockoutMessage
} from './utils'

type EnglishProficiencyActionState = {
  resetTestState: () => void
  setAssessmentId: Dispatch<SetStateAction<number | null>>
  setContinueError: Dispatch<SetStateAction<string | null>>
  setDefaultTest: Dispatch<SetStateAction<EnglishTestDto | null>>
  setInitialAssessmentAnswers: Dispatch<SetStateAction<AssessmentAnswerDto[]>>
  setIsContinuingAfterPass: Dispatch<SetStateAction<boolean>>
  setIsTestModalOpen: Dispatch<SetStateAction<boolean>>
  setRetryLockoutMessage: Dispatch<SetStateAction<string | null>>
  setStartTestError: Dispatch<SetStateAction<string | null>>
  setTestResult: Dispatch<SetStateAction<EnglishTestResult | null>>
  setTestResultOpen: Dispatch<SetStateAction<boolean>>
  testPassed: boolean
  isRetryLockout: boolean
}

type UseEnglishProficiencyActionsArgs = Pick<
  EnglishProficiencyStepProps,
  'onNext' | 'onPassed'
> & {
  state: EnglishProficiencyActionState
}

export function useEnglishProficiencyActions({
  onNext,
  onPassed,
  state
}: UseEnglishProficiencyActionsArgs) {
  const storedSessionToken = useEnrollmentStore(store => store.sessionToken)
  const setStoredAssessmentId = useEnrollmentStore(store => store.setAssessmentId)
  const defaultTestQuery = useDefaultAssessmentTest(false)
  const startAssessmentMutation = useStartAssessment()

  const handleStartTest = useCallback(async () => {
    state.resetTestState()
    state.setContinueError(null)
    const enrollmentSessionToken = storedSessionToken?.trim() ?? ''

    if (!enrollmentSessionToken) {
      state.setStartTestError(ENROLLMENT_COMMON_MESSAGES.sessionMissing)
      state.setContinueError(ENROLLMENT_COMMON_MESSAGES.sessionMissing)
      return
    }

    try {
      const testResponse = await defaultTestQuery.refetch()
      const nextDefaultTest = testResponse.data ?? null

      if (!nextDefaultTest?.id) {
        throw new Error(ENGLISH_TEST_MESSAGES.loadFailed)
      }

      const assessment = await startAssessmentMutation.mutateAsync({
        sessionToken: enrollmentSessionToken,
        testId: nextDefaultTest.id
      })

      const nextAssessmentId = assessment.id ?? null

      if (!nextAssessmentId) {
        throw new Error(ENGLISH_TEST_MESSAGES.startFailed)
      }

      if (assessment.status === EnrollmentAssessmentDtoStatusEnum.Passed) {
        setStoredAssessmentId(null)
        state.setTestResult(createPassedAssessmentResult(assessment, nextDefaultTest))
        state.setTestResultOpen(true)
        return
      }

      state.setDefaultTest(nextDefaultTest)
      state.setAssessmentId(nextAssessmentId)
      state.setInitialAssessmentAnswers(assessment.answers ?? [])
      setStoredAssessmentId(nextAssessmentId)
      state.setIsTestModalOpen(true)
    } catch (error) {
      const errorMessage = formatStartTestErrorMessage(error, ENGLISH_TEST_MESSAGES.startFailed)

      setStoredAssessmentId(null)

      if (isRetryLockoutMessage(errorMessage)) {
        state.setTestResult(null)
        state.setRetryLockoutMessage(errorMessage)
        state.setTestResultOpen(true)
        return
      }

      state.setStartTestError(errorMessage)
      state.setContinueError(errorMessage)
    }
  }, [defaultTestQuery, setStoredAssessmentId, startAssessmentMutation, state, storedSessionToken])

  const handleCloseTest = useCallback(() => {
    state.setIsTestModalOpen(false)
    state.setAssessmentId(null)
    state.setInitialAssessmentAnswers([])
    setStoredAssessmentId(null)
  }, [setStoredAssessmentId, state])

  const handleTestComplete = useCallback((result: EnglishTestResult) => {
    state.setRetryLockoutMessage(null)
    state.setContinueError(null)
    state.setTestResult(result)
    state.setIsTestModalOpen(false)
    state.setTestResultOpen(true)
  }, [state])

  const handleResultAction = useCallback(async () => {
    if (!state.testPassed) {
      if (state.isRetryLockout) {
        window.location.assign(ENROLLMENT_PUBLIC_LINKS.cdlTraining)
        return
      }

      await handleStartTest()
      return
    }

    state.setContinueError(null)
    state.setIsContinuingAfterPass(true)

    try {
      await onPassed?.()
      onNext()
    } catch (error) {
      state.setContinueError(
        getErrorMessage(
          error,
          'We could not refresh your enrollment after the English test. Please try again.'
        )
      )
    } finally {
      state.setIsContinuingAfterPass(false)
    }
  }, [handleStartTest, onNext, onPassed, state])

  return {
    handleCloseTest,
    handleResultAction,
    handleStartTest,
    handleTestComplete,
    isStartingTest: defaultTestQuery.isFetching || startAssessmentMutation.isPending
  }
}
